<template>
  <div id="gap-backtest-shortlist">
    <div class="container-fluid pt-4 px-4">
      <div class="row g-4 mb-4">
        <div class="col-sm-6 col-xl-3">
          <SummaryBox iconType="fa-solid fa-list-check" title="Gap Events" :amount="summary.totalGapEvents" />
        </div>
        <div class="col-sm-6 col-xl-3">
          <SummaryBox type="percent" iconType="fa-solid fa-arrows-left-right" title="Fill Rate" :amount="summary.fadeBiasStats.fillRatePct" />
        </div>
        <div class="col-sm-6 col-xl-3">
          <SummaryBox type="percent" iconType="fa-solid fa-trophy" title="Win Rate" :amount="summary.fadeBiasStats.winRatePct" />
        </div>
        <div class="col-sm-6 col-xl-3">
          <SummaryBox type="percent" iconType="fa-solid fa-chart-line" title="Avg Return / Trade" :amount="summary.fadeBiasStats.avgReturnPct" />
        </div>
      </div>

      <v-alert v-if="summary.caveat" type="warning" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        {{ summary.caveat }}
      </v-alert>

      <v-card flat rounded="lg" class="gap-backtest-card">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Gap Fill Shortlist</span>
          <span v-if="summary.generatedAt" class="text-caption text-medium-emphasis">
            Backtest generated {{ $helper.formatDate(summary.generatedAt) }}
          </span>
        </v-card-title>

        <v-data-table
          v-model:expanded="expanded"
          :headers="headers"
          :items="shortlist"
          item-value="code"
          show-expand
          fixed-header
          :items-per-page-options="[10, 15, 20, 30, 50, 100]"
          :items-per-page="20"
          class="custom-data-table"
          @update:expanded="onExpand"
        >
          <template v-slot:[`item.fillRatePct`]="{ item }">{{ item.fillRatePct }}%</template>

          <template v-slot:[`item.winRatePct`]="{ item }">
            <span :class="item.winRatePct >= 20 ? 'text-success' : 'text-error'">{{ item.winRatePct }}%</span>
          </template>

          <template v-slot:[`item.wilsonLowerBoundPct`]="{ item }">{{ item.wilsonLowerBoundPct }}%</template>

          <template v-slot:[`item.returnLowerBoundPct`]="{ item }">
            <span :class="item.returnLowerBoundPct >= 0 ? 'text-success' : 'text-error'">{{ item.returnLowerBoundPct }}%</span>
          </template>

          <template v-slot:[`item.avgReturnPct`]="{ item }">
            <span :class="item.avgReturnPct >= 0 ? 'text-success' : 'text-error'">{{ item.avgReturnPct }}%</span>
          </template>

          <template v-slot:[`item.avgVolume20`]="{ item }">{{ Number(item.avgVolume20).toLocaleString() }}</template>

          <template v-slot:[`item.avgOpenPrice`]="{ item }">{{ $helper.formatPrice(item.avgOpenPrice) }}</template>

          <template v-slot:[`item.affordable`]="{ item }">
            <v-chip :color="item.affordable ? 'success' : 'default'" size="small" variant="tonal">
              {{ item.affordable ? 'Yes' : 'No' }}
            </v-chip>
          </template>

          <template v-slot:expanded-row="{ columns, item }">
            <tr class="sub-row-wrap">
              <td :colspan="columns.length">
                <div v-if="eventsLoading[item.code]" class="text-caption text-medium-emphasis">Loading events…</div>
                <div v-else-if="eventsByCode[item.code]" class="table-scroll">
                  <table class="sub-table">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Gap %</th>
                        <th>Type</th>
                        <th>Direction</th>
                        <th>Filled</th>
                        <th>Result</th>
                        <th>Return %</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="event in eventsByCode[item.code]" :key="event.gap_backtest_event_id">
                        <td class="sub-cell">{{ event.trade_date }}</td>
                        <td class="sub-cell">{{ Number(event.gap_pct).toFixed(2) }}%</td>
                        <td class="sub-cell">{{ event.gap_type }}</td>
                        <td class="sub-cell">{{ event.direction }}</td>
                        <td class="sub-cell">{{ event.filled ? 'Yes' : 'No' }}</td>
                        <td class="sub-cell">{{ event.simulated_result || '—' }}</td>
                        <td
                          class="sub-cell"
                          :class="event.simulated_return_pct !== null && Number(event.simulated_return_pct) >= 0 ? 'text-success' : 'text-error'"
                        >
                          {{ event.simulated_return_pct !== null ? Number(event.simulated_return_pct).toFixed(2) + '%' : '—' }}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </td>
            </tr>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';
import SummaryBox from '@/components/Summary/Box.ce.vue';

export default {
  name: 'GapBacktestShortlist-page',
  components: {
    SummaryBox,
  },
  data() {
    return {
      summary: {
        totalGapEvents: 0,
        fadeBiasStats: { fillRatePct: 0, winRatePct: 0, avgReturnPct: 0 },
        caveat: '',
        generatedAt: null,
      },
      shortlist: [],
      expanded: [],
      eventsByCode: {},
      eventsLoading: {},
      headers: [
        { title: 'Code', key: 'code' },
        { title: 'Events', key: 'gapEventCount' },
        { title: 'Fill %', key: 'fillRatePct' },
        { title: 'Win %', key: 'winRatePct' },
        { title: 'Wilson LB %', key: 'wilsonLowerBoundPct' },
        { title: 'Avg Return %', key: 'avgReturnPct' },
        { title: 'Avg Vol (20d)', key: 'avgVolume20' },
        { title: 'Avg Price', key: 'avgOpenPrice' },
        { title: 'Max Shares (risk)', key: 'maxSharesByRisk' },
        { title: 'Affordable', key: 'affordable' },
      ],
    };
  },
  mounted() {
    this.loadShortlist();
  },
  methods: {
    loadShortlist() {
      fetchWrapper.get('api/gap-backtest/shortlist').then((res) => {
        if (res?.data) {
          this.summary = res.data;
          this.shortlist = res.data.shortlist || [];
        }
      }).catch((err) => {
        console.error('Failed to load gap backtest shortlist:', err);
      });
    },
    onExpand(expandedKeys) {
      expandedKeys.forEach((code) => {
        if (!this.eventsByCode[code] && !this.eventsLoading[code]) {
          this.eventsLoading = { ...this.eventsLoading, [code]: true };
          fetchWrapper.get(`api/gap-backtest/events?code=${code}`).then((res) => {
            this.eventsByCode = { ...this.eventsByCode, [code]: res?.data || [] };
            this.eventsLoading = { ...this.eventsLoading, [code]: false };
          }).catch((err) => {
            console.error(`Failed to load gap events for ${code}:`, err);
            this.eventsLoading = { ...this.eventsLoading, [code]: false };
          });
        }
      });
    },
  },
};
</script>

<style scoped>
.sub-row-wrap td {
  padding: 12px 16px !important;
  background-color: var(--light);
}

.sub-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.85rem;
  white-space: nowrap;
}

.sub-table thead th {
  text-align: left;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-muted);
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
}

.sub-table .sub-cell {
  padding: 8px 12px;
  border-bottom: 1px solid var(--border);
}

.sub-table tbody tr:last-child .sub-cell {
  border-bottom: none;
}
</style>
